/**
 * context.ts — the shared UI context passed to every panel/component.
 */
import type { Engine } from '../core/Engine';
import type { EventBus } from '../core/EventBus';
import type { Binder } from './dom';
import type { Sfx } from '../audio/sfx';
import { format } from '../core/numbers';

export type BuyAmount = 1 | 10 | 100 | 'max';

export interface UIContext {
  engine: Engine;
  bus: EventBus;
  binder: Binder;
  sfx: Sfx;
  getBuyAmount: () => BuyAmount;
  toast: (kind: 'info' | 'success' | 'warning' | 'reward', message: string) => void;
  /** Re-render dynamic panels (e.g. after a layer unlock changes available content). */
  requestRebuild: () => void;
  /** Show the prestige confirmation modal; runs onConfirm if the player accepts. */
  confirmPrestige: (onConfirm: () => void) => void;
  /** Show the ascension confirmation modal; runs onConfirm if the player accepts. */
  confirmAscend: (onConfirm: () => void) => void;
  /** Transient rolling history (UI-owned, ~1 Hz) for sparklines. */
  history: { income: number[]; price: number[] };
  /** Register a live hover/focus tooltip on an element. */
  tooltip: (el: HTMLElement, getContent: () => string) => void;
}

/** Money/number formatter honouring the player's notation setting. */
export function fmt(ctx: UIContext, n: number): string {
  const notation = ctx.engine.state.settings.notation;
  const abs = Math.abs(n);
  if (abs < 1e6) return format(n); // small numbers always read best as plain/suffix
  switch (notation) {
    case 'scientific':
      return n.toExponential(2);
    case 'engineering': {
      const exp = Math.floor(Math.log10(abs) / 3) * 3;
      return `${(n / Math.pow(10, exp)).toFixed(2)}e${exp}`;
    }
    case 'letters':
    case 'short':
    default:
      return format(n);
  }
}
